/* global Promise */
import {Dropdown, Form, Input, Tooltip} from 'antd';
import moment from 'moment';
import {toast} from 'react-toastify';
import {extractUserId, formatValue, sanitizeInput} from 'utils/helper';
import {instrumentKeyLabel} from 'pages/AssetManagement/modules/instrumentKeys';
import {buildOrderFilterInput} from './ordersFilterInput';
import theme from '../ordersTheme.module.scss';
import {DateCell, Dash, Direction, Num, Pill, StatusPill} from './ui/cells';
import DBModal from 'components/modal/modal';
import {Button} from '@dabafinance/react-components';
import {ModalShell} from './ui/ModalShell';
import orderStyles from './viewOrders.module.scss';
import {
  buildHoldInput,
  HOLD_BULK_LIMIT,
  HOLD_REASON_MAX_LENGTH,
  holdReasonError,
  latestHoldReason,
} from './holdReason';
import {HOLD_ORDER} from 'graphql/mutations/stocks';
import {buildSendNotificationMenuItem} from 'components/sendUserNotification';
import UserLink from 'components/userLink/UserLink';
import {useMutation} from '@apollo/client';
// import {isDev} from 'utils/constants';

const handleStocksOrderFilter = (
  values,
  setFilterValues,
  page,
  user,
  refetch,
  activeTab,
  limit,
  searchTicker,
  searchedValue,
  isAffiliate,
  setOpenFilterModal,
  searchInstrumentId,
  // The exchange lens. A filter submit builds its own input, so without this
  // the current venue would silently drop the moment any filter was applied.
  scopedExchangeId,
) => {
  setFilterValues(values);
  // Everything the panel owns is converted in one place, by declared type.
  // What is deliberately not in there — status, hold, openOrders and
  // includeOrdersWithElapsedExpiry — is decided by the active tab below, so
  // the two never fight over the same key.
  const filterInput = buildOrderFilterInput(values);
  const {status, hold} = values;

  const holdFilter = hold ?? activeTab === 'HOLD';

  refetch({
    input: {
      ...filterInput,
      status: status
        ? status
        : ['PENDING', 'FILLED', 'EXPIRED'].includes(activeTab)
        ? activeTab
        : null,
      limit,
      // An explicit choice in the filter form wins over the page-level lens.
      stockExchange: filterInput.stockExchange || scopedExchangeId || null,
      hold: holdFilter,
      // A ticker searched from the toolbar pins the instrument regardless of
      // what the panel picked.
      instrumentId: searchInstrumentId || filterInput.instrumentId,
      userId: extractUserId(user),
      search: searchTicker || searchedValue || null,
      page: page ?? 1,
      'paginate': true,
      'newFirst': true,
      ...(isAffiliate && {affiliateOrder: isAffiliate}),
      includeOrdersWithElapsedExpiry: true,
      // Held orders always live under status=OPEN, so combining
      // `openOrders: false` (the resolver's shorthand for "closed statuses
      // only": Filled/Cancelled/Expired/Rejected/Error/Settled) with
      // `hold: true` is a contradiction — the resolver returns zero rows
      // even when held orders exist. Callers that don't set an activeTab
      // (e.g. the per-user Orders tab, where the modal owns the hold
      // toggle) previously fell through the ternary below to
      // `openOrders: false`, which silently hid every held order.
      // Whenever the request asks for held orders, drop the openOrders
      // constraint so the resolver's own `hold` handling decides the set.
      openOrders: holdFilter
        ? null
        : activeTab === 'ACTIVE' &&
          !['PARTIALLY_FILLED', 'OPEN'].includes(status)
        ? true
        : [
            'PARTIALLY_FILLED',
            'REJECTED',
            'OPEN',
            'ERROR',
            'CANCELLED',
            'SETTLED',
          ].includes(status) ||
          // ALL is the per-user Orders tab: one person's whole book, open and
          // closed. Without it this fell through to `openOrders: false` and
          // every open or pending order vanished on page 2 or after a filter.
          ['PENDING', 'FILLED', 'HOLD', 'EXPIRED', 'ALL'].includes(activeTab)
        ? null
        : false,
    },
  });
  setOpenFilterModal(false);
};

const handleStocksOrdersColumns = (
  navigate,
  styles,
  gamer,
  tabOperations,
  handleTab,
  setCopied,
  copied,
  dotsIcon,
  // `{allowed, open}` from useSendUserNotification. Optional so the column
  // factory still works for callers that do not offer the action.
  sendNotification,
) => {
  const getOrderMenuItems = (render, tabOperations) => {
    const {status, type, history, notes, hold, id} = render;

    const isOpen = status === 'OPEN';
    const isPending = status === 'PENDING';
    const isFilled = status === 'FILLED';
    const isOpenOrPartiallyFilled = ['OPEN', 'PARTIALLY_FILLED'].includes(
      status,
    );
    const canHold = ['OPEN', 'PENDING', 'PARTIALLY_FILLED', 'FILLED'].includes(
      status,
    );
    const canExpire = isOpenOrPartiallyFilled && type === 'LIMIT';
    const menuItems = [];

    menuItems.push({
      key: 'view-order',
      label: 'View Order',
      onClick: e => handleTab('VIEW_ORDER', id, e),
    });

    if (hold) {
      return [
        {
          key: '1',
          label: 'Release Hold',
          onClick: () => handleTab('ONHOLD', id),
        },
      ];
    }

    if (!tabOperations?.update) {
      return status !== 'PENDING'
        ? [
            {
              key: '5',
              label: 'View History',
              onClick: e => handleTab('HISTORY', history, e),
            },
          ]
        : [];
    }

    // Handle Open/Partially Filled status
    if (isOpenOrPartiallyFilled) {
      menuItems.push(
        {
          key: '1',
          label: 'Reject Order',
          onClick: () => handleTab('REJECT', id),
        },
        {
          key: '2',
          label: 'Fill Order',
          onClick: e => handleTab('SETTLE', id, e),
        },
      );
    }

    // Handle Filled status
    if (isFilled) {
      menuItems.push({
        key: '2',
        label: 'Settle Order',
        onClick: e => handleTab('FILLED', render, e),
      });
    }

    // Handle Pending status
    if (isPending) {
      menuItems.push(
        {
          key: '3',
          label: 'Open Order',
          onClick: e => handleTab('PENDING', id, e),
        },

        {
          key: '4',
          label: 'Reject Order',
          onClick: () => handleTab('REJECT', id),
        },
      );
    }

    // Add common actions
    if (isOpen) {
      menuItems.push({
        key: '6',
        label: 'Revert to Pending',
        onClick: e => handleTab('OPEN', id, e),
      });
    }

    if (!isPending) {
      menuItems.push({
        key: '5',
        label: 'View History',
        onClick: e => handleTab('HISTORY', history, e),
      });
    }

    if (canExpire) {
      menuItems.push({
        key: '7',
        label: 'Expire Order',
        onClick: e => handleTab('OPEN', {id, type: 'EXPIRE'}, e),
      });
    }

    if (!isPending && tabOperations?.update) {
      menuItems.push({
        key: '10',
        label: 'Rewrite Order',
        onClick: () => handleTab('REWRITE', id),
      });
    }

    // Always add Notes option
    menuItems.push({
      key: '8',
      label: 'View / Edit Notes',
      onClick: e => handleTab('NOTES', {notes, id}, e),
    });

    // Add Hold Order option for specified statuses
    if (canHold) {
      menuItems.push({
        key: 'hold',
        label: hold ? 'Remove Hold' : 'Hold Order',
        onClick: () => handleTab(hold ? 'ONHOLD' : 'HOLD', id),
      });
    }

    if (isPending || isOpenOrPartiallyFilled) {
      menuItems.push({
        // Shared the key '5' with View History, and the two co-exist on an
        // open order — antd is free to drop one of a duplicate pair.
        key: 'execute',
        label: 'Execute Order',
        onClick: e => handleTab('EXECUTE', render, e),
        // disabled: isDev && false,
      });
    }
    return menuItems;
  };

  const handleCopy = referenceId => {
    navigator.clipboard.writeText(referenceId).then(() => {
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 400);
    });
  };

  return [
    {
      title: 'Order ID',
      key: 'id',
      dataIndex: 'id',
      width: 260,
      fixed: 'left',
      render: (text, record) => (
        <Tooltip trigger="hover" title={copied ? 'Copied' : 'Copy'}>
          <b
            className={theme.orderId}
            onClick={e => {
              e.stopPropagation();
              handleCopy(record?.id);
            }}>
            {record?.id}
          </b>
        </Tooltip>
      ),
    },
    {
      title: 'User',
      key: 'user',
      dataIndex: 'user',
      width: 190,
      render: (text, record) => (
        <div className={theme.user}>
          <img src={record?.user?.imageUrl || gamer} alt="" />
          <span>
            <UserLink userId={record?.user?.id} tab="transactions?tab=stock">
              {record?.user?.firstName} {record?.user?.lastName}
            </UserLink>
          </span>
        </div>
      ),
    },
    {
      title: 'Ticker',
      key: 'ticker',
      dataIndex: 'ticker',
      // Tickers here run to "ZENITHBANK" and "DBA-CONS-A"; at 100px every one
      // of them wrapped onto a second line.
      width: 160,
      render: (text, render) =>
        render?.symbol ? (
          <span className={theme.ticker}>{render.symbol}</span>
        ) : (
          <Dash />
        ),
    },
    {
      title: 'Direction',
      key: 'direction',
      dataIndex: 'direction',
      width: 100,
      render: (text, record) => <Direction value={record?.direction} />,
    },

    {
      title: 'Instrument',
      key: 'instrumentKey',
      dataIndex: 'instrumentKey',
      width: 140,
      render: (text, render) =>
        render?.instrumentKey ? (
          <Pill>{instrumentKeyLabel(render.instrumentKey)}</Pill>
        ) : (
          <Dash />
        ),
    },
    {
      title: 'Currency',
      key: 'assetCurrency',
      dataIndex: 'assetCurrency',
      width: 100,
      render: (text, record) =>
        record?.assetCurrency ? (
          <span className={theme.tabular}>{record.assetCurrency}</span>
        ) : (
          <Dash />
        ),
    },
    {
      title: 'Order Type',
      key: 'type',
      dataIndex: 'type',
      width: 120,
      render: (text, render) =>
        render?.type ? (
          <Pill tone={render.type === 'LIMIT' ? 'neutral' : 'info'}>
            {render.type}
          </Pill>
        ) : (
          <Dash />
        ),
    },
    {
      title: 'Hold Status',
      key: 'hold',
      dataIndex: 'hold',
      width: 120,
      render: (text, render) => {
        if (!render?.hold) return <Dash />;
        const reason = latestHoldReason(render?.notes);
        return (
          <Tooltip title={reason || 'No reason recorded'}>
            <span>
              <Pill dot tone="stop">
                ON HOLD
              </Pill>
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      dataIndex: 'status',
      width: 150,
      render: (text, render) => <StatusPill status={render?.status} />,
    },
    {
      title: 'Collection',
      key: 'collectionId',
      dataIndex: 'collectionId',
      width: 170,
      render: (text, record) =>
        record?.collection ? (
          <div className={theme.user}>
            <img src={record?.collection?.bannerUrl} alt="" />
            <span>{record?.collection?.name}</span>
          </div>
        ) : (
          <Dash />
        ),
    },

    {
      title: 'Limit Price',
      key: 'stockPrice',
      dataIndex: 'stockPrice',
      width: 120,
      align: 'right',
      render: (text, render) => <Num value={render?.limitPrice} />,
    },
    {
      title: 'Fill Price',
      key: 'avgFillPrice',
      dataIndex: 'avgFillPrice',
      width: 120,
      align: 'right',
      render: (text, render) => <Num value={render?.avgFillPrice} />,
    },
    {
      title: 'Quantity',
      key: 'quantity',
      dataIndex: 'quantity',
      width: 110,
      align: 'right',
      render: (text, record) => <Num value={record?.quantity} />,
    },
    {
      title: 'Order Total (Ex. Fees)',
      key: 'price',
      dataIndex: 'price',
      width: 170,
      align: 'right',
      render: (text, render) => (
        <Num
          value={
            render?.quantity * (render?.avgFillPrice || render?.limitPrice)
          }
        />
      ),
    },
    {
      title: 'Static Fee',
      key: 'staticFee',
      dataIndex: 'staticFee',
      width: 120,
      align: 'right',
      render: (text, record) => <Num value={record?.staticFee} decimals={2} />,
    },
    {
      title: 'Floating Fee',
      key: 'FloatingFee',
      dataIndex: 'floatingFee',
      width: 130,
      align: 'right',
      render: (text, record) => (
        <Num value={record?.floatingFee} decimals={2} />
      ),
    },

    {
      title: 'Recurring Fee',
      key: 'recurringFee',
      dataIndex: 'recurringFee',
      width: 140,
      align: 'right',
      render: (text, record) => (
        <Num value={record?.recurringFee} decimals={2} />
      ),
    },
    {
      title: 'Commission Fee',
      key: 'commissionFee',
      dataIndex: 'commissionFee',
      width: 150,
      align: 'right',
      render: (text, record) => (
        <Num value={record?.commissionFee} decimals={2} />
      ),
    },
    {
      title: 'Exit Fee',
      key: 'exitFee',
      dataIndex: 'exitFee',
      width: 110,
      align: 'right',
      render: (text, record) => <Num value={record?.exitFee} decimals={2} />,
    },
    {
      title: 'Entry Fee',
      key: 'entryFee',
      dataIndex: 'entryFee',
      width: 110,
      align: 'right',
      render: (text, record) => <Num value={record?.entryFee} decimals={2} />,
    },
    {
      title: 'Fee Charged',
      key: 'feeCharged',
      dataIndex: 'feeCharged',
      width: 130,
      align: 'right',
      render: (text, render) => <Num value={render?.feeCharged} decimals={2} />,
    },
    {
      title: 'Expiration Date',
      // Was also keyed `createdAt`, which made two columns share a React key.
      key: 'expiresAt',
      dataIndex: 'createdAt',
      width: 180,
      render: (text, record) => <DateCell value={record?.expiresAt} />,
    },
    {
      title: 'Created Date',
      key: 'createdAt',
      dataIndex: 'createdAt',
      width: 180,
      render: (text, record) => <DateCell value={record?.createdAt} />,
    },
    {
      title: 'Filled Date',
      key: 'updatedAt',
      dataIndex: 'updatedAt',
      width: 180,
      render: (text, record) => <DateCell value={record?.lastFillDate} />,
    },
    {
      title: 'Retry',
      key: 'retry',
      dataIndex: 'retry',
      width: 110,
      render: (text, render) =>
        render?.retry ? <Pill tone="brand">Retried</Pill> : <Dash />,
    },
    {
      title: 'Actions',
      key: 'actions',
      dataIndex: 'actions',
      fixed: 'right',
      width: 72,
      render: (text, render) => {
        // Appended out here rather than inside getOrderMenuItems: that
        // function returns early for held orders and for read-only admins,
        // and notifying the investor is available in both of those states.
        const items = getOrderMenuItems(render, tabOperations);
        const notifyItem = buildSendNotificationMenuItem({
          user: render?.user,
          context: {label: 'Order', reference: render?.symbol || render?.id},
          onOpen: sendNotification?.open,
          allowed: Boolean(sendNotification?.allowed),
        });
        if (notifyItem) items.push(notifyItem);

        return (
          <Dropdown
            trigger={['click']}
            overlayClassName={theme.menu}
            menu={{items}}>
            <img src={dotsIcon} onClick={e => e.stopPropagation()} alt="" />
          </Dropdown>
        );
      },
    },
  ];
};

const bulkOperationItems = (
  tab,
  handleOperation,
  disabled,
  setOrderModal,
  additionalCheck,
) => {
  const defaultTabs = [
    {
      key: '1',
      label: 'Bulk Fill Selected Orders',
      disabled,
      onClick: () => handleOperation('SETTLE'),
    },
    {
      key: '2',
      label: 'Bulk Fill CSV Orders',
      onClick: () => setOrderModal('FILL_CSV'),
    },
    {
      key: '3',
      label: 'Bulk Revert Selected Orders to Pending',
      disabled,
      onClick: () => handleOperation('OPEN'),
    },
    {
      key: '5',
      label: 'Bulk Expire Orders',
      disabled: disabled || additionalCheck,
      onClick: () => handleOperation('OPEN', 'EXPIRE'),
    },
    {
      key: '6',
      label: 'Bulk Reject Open Orders',
      disabled,
      onClick: () => handleOperation('REJECT'),
    },
  ];

  if (tab === 'PENDING') {
    return [
      {
        key: 1,
        disabled,
        label: 'Open Selected Orders',
        onClick: () => handleOperation('PENDING'),
      },
      {
        key: 'hold',
        label: 'Bulk Hold Selected Orders',
        disabled,
        onClick: () => handleOperation('HOLD'),
      },
      {
        key: 'onhold',
        label: 'Bulk Release Selected Orders On Hold',
        disabled,
        onClick: () => handleOperation('ONHOLD'),
      },
    ];
  } else if (tab === 'HOLD') {
    return [
      {
        key: 'onhold',
        label: 'Bulk Release Selected Orders On Hold',
        disabled,
        onClick: () => handleOperation('ONHOLD'),
      },
    ];
  } else if (tab === 'FILLED') {
    return [
      {
        key: 1,
        disabled,
        label: 'Bulk Settle Selected Orders',
        onClick: () => handleOperation('FILLED'),
      },
      {
        key: 2,
        label: 'Bulk Settle CSV Orders',
        onClick: () => setOrderModal('SETTLE_CSV'),
      },
      {
        key: 'hold',
        label: 'Bulk Hold Selected Orders',
        disabled,
        onClick: () => handleOperation('HOLD'),
      },
      {
        key: 'onhold',
        label: 'Bulk Release Selected Orders On Hold',
        disabled,
        onClick: () => handleOperation('ONHOLD'),
      },
    ];
  } else if (tab === 'ACTIVE') {
    return [
      ...defaultTabs,
      {
        key: 'hold',
        label: 'Bulk Hold Selected Orders',
        disabled,
        onClick: () => handleOperation('HOLD'),
      },
      {
        key: 'onhold',
        label: 'Bulk Release Selected Orders On Hold',
        disabled,
        onClick: () => handleOperation('ONHOLD'),
      },
    ];
  } else {
    return [];
  }
};

const handleSearchStockTicker = (
  searchTerm,
  user,
  active,
  pageLimit,
  existingFilters,
  refetch,
  currentPage,
  isAffiliate,
  handleResetPage,
) => {
  // Check for double preceding whitespace or double whitespace
  const sanitizedValue = sanitizeInput(searchTerm);

  let {
    startDate,
    endDate,
    collection,
    collectionId,
    startExpiryDate,
    endExpiryDate,
    status,
    type,
    affiliateOrder,
    stockExchange,
    instrumentKey,
    hold,
    instrumentId,
    direction,
    startSettlementDate,
    endSettlementDate,
    startFillDate,
    endFillDate,
  } = existingFilters;

  const formattedValues = {
    userId: extractUserId(user),
    startDate: formatValue(startDate, d => moment(d).format()),
    endDate: formatValue(endDate, d => moment(d).format()),
    startExpiryDate: formatValue(startExpiryDate, d => moment(d).format()),
    endExpiryDate: formatValue(endExpiryDate, d => moment(d).format()),
    startSettlementDate: formatValue(startSettlementDate, d =>
      moment(d).format(),
    ),
    endSettlementDate: formatValue(endSettlementDate, d => moment(d).format()),
    startFillDate: formatValue(startFillDate, d => moment(d).format()),
    endFillDate: formatValue(endFillDate, d => moment(d).format()),
  };

  handleResetPage();
  refetch({
    input: {
      ...formattedValues,
      collection,
      collectionId,
      type,
      direction,
      instrumentKey,
      hold: hold ?? active === 'HOLD',
      stockExchange,
      instrumentId,
      affiliateOrder,
      status: ['PENDING', 'FILLED', 'EXPIRED'].includes(status || active)
        ? status || active
        : null,
      search: sanitizedValue.toUpperCase() ?? null,
      page: currentPage,
      'paginate': true,
      limit: pageLimit,
      'newFirst': true,
      ...(isAffiliate && {affiliateOrder: isAffiliate}),
      includeOrdersWithElapsedExpiry: true,
      openOrders:
        active === 'ACTIVE' && !['PARTIALLY_FILLED', 'OPEN'].includes(status)
          ? true
          : ['PENDING', 'FILLED', 'HOLD', 'EXPIRED'].includes(active) ||
            ['PARTIALLY_FILLED', 'OPEN'].includes(status)
          ? null
          : false,
    },
  });
};

const handleOrderRewrite = ({
  rewriteOrder,
  refetch,
  setOrder,
  setOrderModal,
  orderId,
}) => {
  rewriteOrder({
    variables: {
      orderId,
    },
  })
    .then(({data: {rewrite}}) => {
      if (rewrite.statusCode === 400 || rewrite.__typename === 'Error') {
        toast.error(rewrite?.message);
      } else {
        toast.success('Order Rewrite Successful');
        refetch();
        setOrder('');
        setOrderModal(false);
      }
    })
    .catch(error => toast.error(error));
};

const HoldOrderModal = ({
  holdStatus,
  orderId,
  setOrder,
  setOrderModal,
  refetch,
  setOrderDetails,
  orderModal,
  orderIds,
  clearOrderIds,
  operationType,
  clearOperationType,
}) => {
  const [form] = Form.useForm();
  const [holdOrder, {loading}] = useMutation(HOLD_ORDER, {
    onError: error => toast.error(error.message),
    errorPolicy: 'all',
  });

  const hold = holdStatus === 'HOLD';
  const ids = operationType === 'MULTIPLE' ? orderIds || [] : [orderId];
  const count = ids.filter(Boolean).length;
  const subject = count > 1 ? `these ${count} orders` : 'this order';

  const close = () => {
    form.resetFields();
    setOrderModal(false);
    setOrder('');
    setOrderDetails({});
    clearOrderIds();
    clearOperationType();
  };

  const submit = ({reason}) => {
    holdOrder({
      variables: {input: buildHoldInput({orderIds: ids, hold, reason})},
    })
      .then(({data}) => {
        const result = data?.holdOrder;
        if (!result) return;
        if (result.statusCode === 400 || result.__typename === 'Error') {
          toast.error(result?.message);
        } else {
          toast.success(result?.message);
          refetch();
          close();
        }
      })
      .catch(error => toast.error(error?.message || String(error)));
  };

  return (
    <DBModal
      isOpen={orderModal}
      handleClose={close}
      content={
        <ModalShell
          title={hold ? 'Hold Order(s)' : 'Release Hold(s)'}
          subtitle={
            hold
              ? `The reason is saved as a note on ${subject} and in its history.`
              : `Optional. If given, the reason is saved as a note on ${subject}.`
          }
          footer={
            <>
              <Button onClick={close} type="secondary" label="Cancel" />
              <Button
                disabled={loading || !count}
                onClick={() => form.submit()}
                label={loading ? 'Working…' : hold ? 'Hold' : 'Release'}
              />
            </>
          }>
          <div className={orderStyles.confirmBody}>
            {hold
              ? `Put ${subject} on hold?`
              : `Release the hold on ${subject}?`}
            {count > HOLD_BULK_LIMIT &&
              ` Only ${HOLD_BULK_LIMIT} orders can be held or released at a time.`}
          </div>
          <Form
            form={form}
            layout="vertical"
            className={theme.fieldStack}
            onFinish={submit}>
            <Form.Item
              name="reason"
              label={hold ? 'Reason' : 'Reason (optional)'}
              rules={[
                {
                  validator: (_, value) => {
                    const error = holdReasonError(value, hold);
                    return error
                      ? Promise.reject(new Error(error))
                      : Promise.resolve();
                  },
                },
              ]}>
              <Input.TextArea
                rows={4}
                maxLength={HOLD_REASON_MAX_LENGTH}
                showCount
                placeholder={
                  hold
                    ? 'e.g. Wallet frozen pending AML review'
                    : 'e.g. AML review cleared'
                }
              />
            </Form.Item>
          </Form>
        </ModalShell>
      }
    />
  );
};

const getUserOrdersMenuItems = (render, handleTab, pageOperations) => {
  const items = [];
  const allowHoldStatuses = ['OPEN', 'PENDING', 'PARTIALLY_FILLED', 'FILLED'];

  items.push({
    key: '0',
    label: 'View Order',
    onClick: e => handleTab('VIEW_ORDER', render.id, e),
  });

  if (pageOperations?.update) {
    if (render?.hold) {
      items.push({
        key: '1',
        label: 'Release Hold',
        onClick: () => handleTab('ONHOLD', render.id),
      });
    }

    if (['OPEN', 'PARTIALLY_FILLED'].includes(render?.status)) {
      items.push(
        {
          key: '2',
          label: 'Reject Order',
          onClick: () => handleTab('REJECT', render.id),
        },
        {
          key: '3',
          label: 'Fill Order',
          onClick: e => handleTab('SETTLE', render.id, e),
        },
      );
    } else if (render?.status === 'PENDING') {
      items.push({
        key: '3',
        label: 'Open Order',
        onClick: e => handleTab('PENDING', render.id, e),
      });
    }

    if (render?.status === 'FILLED') {
      items.push({
        key: '4',
        label: 'Settle Order',
        onClick: e => handleTab('FILLED', render, e),
      });
    }

    // Add Hold Order option only for specified statuses
    if (allowHoldStatuses.includes(render?.status) && !render?.hold) {
      items.push({
        key: '5',
        label: 'Hold Order',
        onClick: () => handleTab('HOLD', render.id),
      });
    }
  }
  items.push({
    key: '6',
    label: 'View History',
    onClick: e => handleTab('HISTORY', render?.history, e),
  });

  return items;
};

export {
  handleStocksOrderFilter,
  HoldOrderModal,
  bulkOperationItems,
  handleOrderRewrite,
  handleStocksOrdersColumns,
  getUserOrdersMenuItems,
  handleSearchStockTicker,
};
